import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ReservationDetailsPage from '../ReservationDetailsPage';
import { reservationService } from '../../services/reservationService';
import './ReservationDetailsModal.css';

/**
 * Construire une URL d'image complète avec la base URL de l'API
 */
const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';
  return `${baseUrl}${url}`;
};

/**
 * Wrapper de sécurité pour empêcher les erreurs de messagerie et d'images
 */
const SafeReservationWrapper = ({ reservation, onClose, currentUser }) => {
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef(null);
  
  // Préparer les données pour le composant ReservationDetailsPage
  const listing = reservation?.listing || {};

  // Fonction pour masquer les boutons QR après le rendu
  const hideQRButtons = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    
    // Masquer tous les boutons qui contiennent "QR", "Générer" ou "Scanner"
    const buttons = container.querySelectorAll('button');
    buttons.forEach(button => {
      const buttonText = button.textContent?.toLowerCase() || '';
      const hasQRIcon = button.querySelector('svg[data-testid*="QrCode"]') || 
                        button.querySelector('svg[data-testid*="QrCodeScanner"]');
      const hasQRText = buttonText.includes('qr') || 
                        buttonText.includes('générer') || 
                        buttonText.includes('scanner') ||
                        buttonText.includes('pickup') ||
                        buttonText.includes('return');
      
      if (hasQRIcon || hasQRText) {
        button.style.display = 'none !important';
        button.style.visibility = 'hidden !important';
        button.setAttribute('aria-hidden', 'true');
        console.log('🚫 Bouton QR masqué:', buttonText);
      }
    });

    // Masquer également les sections spécifiques QR
    const qrElements = container.querySelectorAll(
      '.qr-section, .qr-action-button, [class*="qr-"], [id*="qr-"]'
    );
    qrElements.forEach(el => {
      el.style.display = 'none !important';
      el.style.visibility = 'hidden !important';
    });
  }, []);

  // Observer pour détecter les changements DOM et masquer les nouveaux boutons QR
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new MutationObserver(() => {
      // Délai pour laisser le temps au rendu de se terminer
      setTimeout(hideQRButtons, 50);
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true
    });

    // Masquer immédiatement les boutons existants
    setTimeout(hideQRButtons, 100);

    return () => observer.disconnect();
  }, [hideQRButtons]);
  
  // Error boundary pour capturer les erreurs du composant enfant
  useEffect(() => {
    const handleError = (error) => {
      console.warn('⚠️ Error caught in SafeReservationWrapper:', error);
      if (error.message?.includes('Cannot read properties of null')) {
        setHasError(true);
      }
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);
  
  if (hasError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body1">
            Certaines fonctionnalités interactives ont été désactivées en mode lecture seule.
          </Typography>
        </Alert>
        <Typography variant="h6" sx={{ mb: 2 }}>
          📋 Détails de la Réservation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ID: {reservation.id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Statut: {reservation.status}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Montant: {reservation.total_amount} {reservation.currency}
        </Typography>
        {listing.title && (
          <Typography variant="body2" color="text.secondary">
            Équipement: {listing.title}
          </Typography>
        )}
      </Box>
    );
  }
  
  return (
    <div ref={containerRef} className="readonly-reservation-wrapper" style={{ height: '100%', overflow: 'auto' }}>
      <ReservationDetailsPage
        reservation={reservation}
        listing={listing} // Passer explicitement les données du listing
        currentUser={currentUser || { 
          id: 'modal-readonly-user', 
          role: 'renter',
          name: 'Utilisateur Consultation'
        }}
        onBack={onClose}
        userRole="renter"
        isReadOnlyModal={false} // Permettre l'interactivité pour la messagerie
        onDataUpdate={() => console.log('Read-only mode: onDataUpdate disabled')}
        onReservationRefresh={() => console.log('Read-only mode: onReservationRefresh disabled')}
        // Permettre la messagerie mais désactiver les QR codes
        disableMessaging={false} // ✅ PERMETTRE la messagerie
        disableImageUpload={true} // ❌ Désactiver l'upload d'images  
        disableQRGeneration={true} // ❌ Désactiver la génération QR
        readOnlyMode={false} // ✅ Permettre l'interactivité limitée
      />
    </div>
  );
};

/**
 * Modal pour afficher les détails d'une réservation en lecture seule
 * Réutilise ReservationDetailsPage avec CSS override pour masquer les actions
 */
const ReservationDetailsModal = ({ 
  reservationId, 
  open, 
  onClose,
  currentUser = null // Utilisateur actuel pour la messagerie
}) => {
  const { t } = useTranslation(['financial', 'dashboard']);
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch de la réservation à la demande
  const fetchReservation = async () => {
    if (!reservationId || !open) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Fetching reservation details for ID:', reservationId);
      const response = await reservationService.getReservationById(reservationId);
      
      console.log('📦 Raw API response:', response);
      
      // Gérer différentes structures de réponse possibles
      let reservationData = null;
      
      if (response?.data?.data?.reservation) {
        // Structure: { data: { data: { reservation: reservation } } }
        reservationData = response.data.data.reservation;
      } else if (response?.data?.data) {
        // Structure: { data: { data: reservation } }
        reservationData = response.data.data;
      } else if (response?.data) {
        // Structure: { data: reservation }
        reservationData = response.data;
      } else if (response) {
        // Structure directe: reservation
        reservationData = response;
      }
      
      console.log('🎯 Extracted reservation data:', reservationData);
      
      if (reservationData && (reservationData.id || reservationData.reservation_id)) {
        // Préparer les données pour ReservationDetailsPage
        const preparedReservation = {
          ...reservationData,
          // Assurer que le listing a les bonnes propriétés d'images
          listing: reservationData.listing ? {
            ...reservationData.listing,
            // Convertir les images pour le format attendu par ReservationDetailsPage
            images: reservationData.listing.images?.map(img => ({
              id: img.id,
              url: getFullImageUrl(img.image_url), // URL complète
              image_url: getFullImageUrl(img.image_url), // URL complète
              order_index: img.order_index
            })) || [],
            // Ajouter mainImageUrl si pas présente
            mainImageUrl: (() => {
              const firstImageUrl = reservationData.listing.images?.[0]?.image_url;
              return getFullImageUrl(firstImageUrl);
            })(),
            // Ajouter primary_image comme alias
            primary_image: (() => {
              const imageUrl = reservationData.listing.primary_image ||
                              reservationData.listing.mainImageUrl || 
                              reservationData.listing.images?.[0]?.image_url;
              return getFullImageUrl(imageUrl);
            })()
          } : null
        };
        
        setReservation(preparedReservation);
        console.log('✅ Reservation details loaded successfully');
        console.log('🖼️ Prepared images:', preparedReservation.listing?.images);
        console.log('🖼️ Main image URL:', preparedReservation.listing?.mainImageUrl);
        console.log('🖼️ Primary image:', preparedReservation.listing?.primary_image);
        
        // Debug spécifique pour l'image
        console.log('🔍 Debug image sources for ReservationDetailsPage:');
        console.log('  - listing.primary_image:', preparedReservation.listing?.primary_image);
        console.log('  - listing.mainImageUrl:', preparedReservation.listing?.mainImageUrl);
        console.log('  - listing.images?.[0]?.url:', preparedReservation.listing?.images?.[0]?.url);
        console.log('  - Fallback: /placeholder-image.jpg (should be blocked by CSS)');
      } else {
        throw new Error(`Structure de données inattendue. Response: ${JSON.stringify(response)}`);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement de la réservation:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError(error.message || 'Erreur lors du chargement des détails de la réservation');
    } finally {
      setLoading(false);
    }
  };

  // Charger la réservation quand la modal s'ouvre
  useEffect(() => {
    if (open && reservationId) {
      fetchReservation();
    } else {
      // Reset quand la modal se ferme
      setReservation(null);
      setError(null);
    }
  }, [reservationId, open]);

  // Handler global pour empêcher les erreurs d'images placeholder
  useEffect(() => {
    if (!open) return;
    
    const handleImageError = (event) => {
      const img = event.target;
      if (img.tagName === 'IMG' && img.src.includes('placeholder-image.jpg')) {
        console.log('🚫 Blocked placeholder-image.jpg request, using fallback');
        event.preventDefault();
        event.stopPropagation();
        // Remplacer par une image de fallback
        img.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle" font-family="Arial" font-size="14" fill="%23999"%3E📷 Image non disponible%3C/text%3E%3C/svg%3E';
      }
    };
    
    // Capturer les erreurs d'images dans la modal
    document.addEventListener('error', handleImageError, true);
    
    return () => {
      document.removeEventListener('error', handleImageError, true);
    };
  }, [open]);

  // Handle de fermeture
  const handleClose = () => {
    setReservation(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '100vh',
          maxHeight: '100vh',
          // 🔥 CSS OVERRIDE POUR MODE LECTURE SEULE
          '& .MuiDialog-paper': {
            overflow: 'hidden',
          },
          // Masquer TOUS les boutons qui contiennent des icônes QR
          '& .MuiButton-startIcon .MuiSvgIcon-root[data-testid="QrCodeIcon"]': {
            '& ~ *, &': { display: 'none !important' }
          },
          '& .MuiButton-startIcon .MuiSvgIcon-root[data-testid="QrCodeScannerIcon"]': {
            '& ~ *, &': { display: 'none !important' }
          },
          // Méthode directe: masquer les boutons par leur contenu/enfants
          '& .MuiButton-root': {
            '&:nth-of-type(n)': {
              '& .MuiSvgIcon-root + .MuiButton-label': {
                '&:contains("QR"), &:contains("Générer"), &:contains("Scanner")': {
                  display: 'none !important'
                }
              }
            }
          },
          // Approche par attributs - masquer les boutons contenant QR dans leur structure
          '& button[class*="MuiButton"]': {
            '&:has([data-testid*="QrCode"])': {
              display: 'none !important'
            }
          },
          // Masquer les sections QR complètes
          '& .qr-section': { 
            display: 'none !important' 
          },
          '& .qr-action-button': { 
            display: 'none !important' 
          },
          // NE PAS masquer la messagerie - elle doit rester fonctionnelle
          // '& .messaging-section': { 
          //   display: 'none !important' 
          // },
          // '& [class*="messaging"]': { 
          //   display: 'none !important' 
          // },
          // '& [class*="MessagingInterface"]': { 
          //   display: 'none !important' 
          // },
          // Masquer les sections de paiements et dépôts (déjà affichées dans l'historique)
          '& .payments-section': { 
            display: 'none !important' 
          },
          '& .deposit-section': { 
            display: 'none !important' 
          },
          '& [class*="payment-history"]': { 
            display: 'none !important' 
          },
          // Masquer les images défaillantes
          '& img[src*="placeholder-image.jpg"]': { 
            display: 'none !important' 
          },
          '& .image-upload': { 
            display: 'none !important' 
          },
          '& .image-gallery': { 
            '& img[src*="placeholder"]': {
              display: 'none !important' 
            }
          },
          // Garder seulement les boutons de navigation
          '& button[aria-label*="back"]': { 
            display: 'flex !important' 
          },
          '& .MuiIconButton-root[onClick*="handleClose"]': {
            display: 'flex !important'
          },
          
        }
      }}
    >
      {/* En-tête custom avec bouton fermer */}
      <Box 
        sx={{ 
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1300, // Au-dessus du contenu
          backgroundColor: 'background.paper',
          borderRadius: '50%',
          boxShadow: 2
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{ 
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
          aria-label="Fermer les détails de la réservation"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              gap: 2
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body1" color="text.secondary">
              {t('financial:payment.loadingDetails', 'Chargement des détails de la réservation...')}
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body1">
                {error}
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              ID de réservation : {reservationId}
            </Typography>
          </Box>
        ) : reservation ? (
          <SafeReservationWrapper 
            reservation={reservation} 
            onClose={handleClose}
            currentUser={currentUser}
          />
        ) : (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {t('financial:payment.reservationNotFound', 'Réservation non trouvée')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReservationDetailsModal;
